import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  LoadingIcon,
  Modal,
  Tooltip,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  EDIT_MY_ORG_ENDPOINT_DIALOG_CANCEL,
  EDIT_MY_ORG_ENDPOINT_DIALOG_ENDPOINT,
  EDIT_MY_ORG_ENDPOINT_DIALOG_NAME,
  EDIT_MY_ORG_ENDPOINT_DIALOG_PASSWORD,
  EDIT_MY_ORG_ENDPOINT_DIALOG_SUBMIT,
  EDIT_MY_ORG_ENDPOINT_DIALOG_USERNAME,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { ConnectionStatus, clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useEditMyOrganizationConnectionMutation } from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(
  DialogType.EDIT_MY_ORGANIZATION_ENDPOINT
);

export const EditMyOrgEndpointDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);
  const { connection } = useCurrentConnection(editIndex.toString(), org);
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );

  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();
  const {
    isLoading: isEditing,
    mutate: editConnection,
  } = useEditMyOrganizationConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: () => {
      invalidateConnection();
      dispatch(closedDialog());
      dispatch(clearConnection());
    },
  });

  const [name, setName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');

  React.useEffect(() => {
    if (connection) {
      setName(connection.name);
      setUsername(connection.username);
      setPassword('');
    }
  }, [connection]);

  const handleClose = () => {
    dispatch(closedDialog());
    resetValidation();
    setName('');
    setUsername('');
    setPassword('');
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const trimmedUsername = username.trim();

    if (
      !connection ||
      !trimmedName ||
      (trimmedUsername && !password) ||
      (!trimmedUsername && password)
    ) {
      return;
    }

    // Test connection with new password, it is important that we remove the
    // existing token if there is one because the Stardog.js client will prefer
    // using that and we'll just get a 401 making the user super mad and throw
    // stuff. We don't want to be responsible for broken screens or coffee mugs.
    const newConnection = {
      endpoint: connection.endpoint,
      token: undefined,
      username,
      password,
      useSSO: false,
      name: trimmedName,
      status: ConnectionStatus.DISCONNECTED,
    };

    validate(newConnection, {
      onError,
      onSuccess: (data) => {
        editConnection({
          input: {
            id: connection.id || '',
            name,
            org_domain: org,
            token: data.token,
            username,
          },
        });
      },
    });
  };

  let submitTooltip = '';
  if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  } else if (username && !password) {
    submitTooltip =
      copy.components.endpoints.inputs.password.validationRequiredWithUsername;
  } else if (!username && password) {
    submitTooltip =
      copy.components.endpoints.inputs.username.validationRequired;
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={EDIT_MY_ORG_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.editMyOrgEndpointDialog.buttons.cancel}
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isValidating || isEditing || Boolean(submitTooltip)}
              leftIcon={isValidating || isEditing ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={EDIT_MY_ORG_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.endpoints.editMyOrgEndpointDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.editMyOrgEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            className="endpoint-dialog-input-help-icon-hidden"
            clearable={false}
            helpProps={{ text: copy.components.endpoints.inputs.name.help }}
            id="sd-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required
            size="md"
            testId={EDIT_MY_ORG_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <Input
            clearable={false}
            disabled
            id="sd-connection-endpoint"
            label={copy.components.endpoints.inputs.endpoint.label}
            size="md"
            testId={EDIT_MY_ORG_ENDPOINT_DIALOG_ENDPOINT}
            value={connection?.endpoint || ''}
          />

          <Input
            autoComplete="username"
            clearable={false}
            id="sd-connection-username"
            label={copy.components.endpoints.inputs.username.label}
            onChange={(e) => setUsername(e.target.value)}
            size="md"
            testId={EDIT_MY_ORG_ENDPOINT_DIALOG_USERNAME}
            value={username}
          />

          <Input
            autoComplete="password"
            clearable={false}
            id="sd-connection-password"
            label={copy.components.endpoints.inputs.password.label}
            onChange={(e) => setPassword(e.target.value)}
            size="md"
            testId={EDIT_MY_ORG_ENDPOINT_DIALOG_PASSWORD}
            type="password"
            value={password}
          />

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
